import { ORDER_TYPE } from '@exam-management/common/utils';
import {
  type ExamUsageStatus,
  FilterOperator,
  QSetStatus,
  useGetPublishedQSetsQuery,
} from '@exam-management/graphql';
import { useNavigate, useParams } from '@exam-management/router';
import { type GridRowSelectionModel, type GridCellParams } from '@shared/components';
import { useMemo } from 'react';

import { BaseQSetList } from '../../components';
import { useQSetListSearchParams } from '../../hooks';
import { usePublishedQSetListModal } from '../../providers';

import { columns } from './columns';
import PublishedQSetListNoRowsOverlay from './published-qset-list-no-rows-overlay';

export default function PublishedQSetList() {
  const { courseId } = useParams('/exam-courses/:courseId/qsets');
  const navigate = useNavigate();
  const { onSelectQSet, selectedQSetIds } = usePublishedQSetListModal();
  const [
    { page, pageSize, sortOrder, sortBy },
    { changePageNumber, changePageSize, changeSorting },
  ] = useQSetListSearchParams();

  const { data: publishedQSetsResult, loading: isSearchQSetsLoading } = useGetPublishedQSetsQuery({
    variables: {
      searchQSetsInput: {
        paging: {
          pageSize: pageSize,
          currentPage: page,
        },
        order: {
          type: ORDER_TYPE[sortOrder],
          field: sortBy,
        },
        filter: {
          conditions: [
            {
              value: courseId,
              operator: FilterOperator.Equal,
              field: 'examCourseId',
            },
            {
              value: QSetStatus.Published,
              operator: FilterOperator.Equal,
              field: 'status',
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
  });

  const totalQSets = publishedQSetsResult?.excSearchQSets.total ?? 0;
  const qSets = useMemo(
    () =>
      (publishedQSetsResult?.excSearchQSets.qSets ?? []).map((qSet) => ({
        id: qSet.id,
        code: qSet.code,
        status: qSet.status,
        updatedAt: new Date(qSet.updatedAt),
        lastPublishedAt: qSet.lastPublishedAt ? new Date(qSet.lastPublishedAt) : null,
        examUsageStatus: qSet.examUsageStatus,
        qConfig: {
          code: qSet.qConfig.code,
          name: qSet.qConfig.name,
        },
      })),
    [publishedQSetsResult?.excSearchQSets.qSets],
  );

  const handleCellClick = (cell: GridCellParams) => {
    if (!['moreAction', '__check__'].includes(cell.field)) {
      navigate('/exam-courses/:courseId/qsets/:qSetId', {
        params: {
          courseId,
          qSetId: cell.row.id,
        },
      });
    }
  };

  const examUsageStatusMapByQSetId = useMemo(() => {
    return new Map(qSets.map((qSet) => [qSet.id, qSet.examUsageStatus]));
  }, [qSets]);

  const handleRowSelectionModelChange = (selection: GridRowSelectionModel) => {
    const { selectedQSetIds, qsetExamUsageStatuses } = selection.reduce(
      (acc, id) => {
        const qSetId = String(id);
        const examUsageStatus = examUsageStatusMapByQSetId.get(qSetId) ?? null;
        acc.selectedQSetIds.push(qSetId);
        acc.qsetExamUsageStatuses.push(examUsageStatus);
        return acc;
      },
      { selectedQSetIds: [] as string[], qsetExamUsageStatuses: [] as (ExamUsageStatus | null)[] },
    );
    onSelectQSet(selectedQSetIds, qsetExamUsageStatuses);
  };

  return (
    <BaseQSetList
      columns={columns}
      rows={qSets}
      hideFooter={totalQSets === 0}
      loading={isSearchQSetsLoading}
      checkboxSelection
      keepNonExistentRowsSelected
      slots={{
        noRowsOverlay: PublishedQSetListNoRowsOverlay,
      }}
      onCellClick={handleCellClick}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      rowSelectionModel={selectedQSetIds}
      slotProps={{
        footer: {
          total: totalQSets,
          pagination: {
            page: page,
            count: totalQSets,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];

        if (sortModel?.sort && sortModel?.field) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }
        // Reset sorting to default
        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
